import { useEffect } from 'react';
import {
  useCurrentUser,
  registerLocaleResolver,
  resolvePreferredLocale,
} from '@tyndo/hooks';
import i18n, { SUPPORTED_LOCALES } from './i18n';

/**
 * Resolve the active locale from the tenant/user context and apply it.
 *
 * Precedence (highest first):
 *   1. The authenticated user's IdP-asserted locale — the OIDC `locale` claim
 *      decoded from the JWT (`useCurrentUser().locale`), also surfaced by the
 *      gateway on `GET /api/v1/auth/me`.
 *   2. The active tenant's default locale — SLOTTED IN HERE once the gateway's
 *      `/auth/tenants` (or tenant context) surfaces `DefaultLocale`; omitted
 *      today because that field isn't threaded to the client yet.
 *   3. The browser's `navigator.language`.
 *
 * The candidate list is then CLAMPED to {@link SUPPORTED_LOCALES} by
 * {@link resolvePreferredLocale}: a locale with no resource bundle is never
 * applied, so we never advertise a language we can't render. Because the
 * resolution is real, shipping a new bundle + adding its locale to
 * `SUPPORTED_LOCALES` is all it takes for a user whose claim already says
 * `de-DE` to get German — no code change at the call sites.
 *
 * The resolved locale is applied two ways so React and non-React paths agree:
 *   - `registerLocaleResolver` feeds the non-hook `resolveActiveLocale()` used
 *     by helper/formatter code (the same seam as `registerPlantTimeZoneResolver`).
 *   - `i18n.changeLanguage` re-renders every `t()` / `useActiveLocale()` consumer.
 */
export function useLocaleBootstrap(): void {
  const user = useCurrentUser();
  const userLocale = user?.locale ?? null;

  useEffect(() => {
    const browser =
      typeof navigator !== 'undefined' && navigator.language
        ? navigator.language
        : null;

    // Tenant default would slot between user and browser here (see docstring).
    const resolved = resolvePreferredLocale([userLocale, browser], SUPPORTED_LOCALES);

    // Keep the imperative formatter path (`resolveActiveLocale()`) aligned with
    // what the React tree renders under.
    registerLocaleResolver(() => resolved);

    if (i18n.language !== resolved) {
      void i18n.changeLanguage(resolved);
    }
  }, [userLocale]);
}
